import type { CurrentSession } from '@elite-residence/api-client';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { useApiClient } from '../api/ApiClientProvider';
import { STATUS_LABELS } from '../booking/BookingPages';
import { loadBookings } from '../booking/loadBookings';
import { type Dictionary, useLocale, useText } from '../i18n/LocaleProvider';
import { Icon } from '../ui/icons';
import { readResponse } from '../ui/ui';

// Global search (approved prototype top bar, Ctrl K). Only what the existing
// API and the signed-in staff member's permissions already allow:
// - bookings by reference: the permitted booking list (booking.view), filtered here;
// - customers by name, reference, phone or email: searchCustomers (customer.basic_view);
// - units by number: listUnits within the inventory scope.
// Identity-number search is a separate sensitive capability and is not offered.

const text: Dictionary<'label' | 'placeholder' | 'bookings' | 'customers' | 'units' | 'none' | 'searching' | 'failed'> =
  {
    en: {
      label: 'Search bookings, customers, units',
      placeholder: 'Search bookings, customers, units',
      bookings: 'Bookings',
      customers: 'Customers',
      units: 'Units',
      none: 'No results',
      searching: 'Searching…',
      failed: 'Search is not available right now.',
    },
    ar: {
      label: 'ابحث في الحجوزات والعملاء والوحدات',
      placeholder: 'ابحث في الحجوزات والعملاء والوحدات',
      bookings: 'الحجوزات',
      customers: 'العملاء',
      units: 'الوحدات',
      none: 'لا توجد نتائج',
      searching: 'جارٍ البحث…',
      failed: 'البحث غير متاح حالياً.',
    },
  };

function useDebounced(value: string, delay: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export function GlobalSearch({ session }: { readonly session: CurrentSession }) {
  const client = useApiClient();
  const t = useText(text);
  const { locale } = useLocale();
  const listId = useId();
  const input = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState('');
  const [open, setOpen] = useState(false);
  const query = useDebounced(value.trim(), 300);
  const active = query.length >= 2;
  const location = useLocation();
  const has = (key: string) => session.permissions.includes(key);
  const canProperty = ['property.setup', 'property.reference_price', 'unit.archive'].some(has);

  // A navigation closes the results.
  const path = location.pathname;
  const [openedOn, setOpenedOn] = useState(path);
  if (openedOn !== path) {
    setOpenedOn(path);
    setOpen(false);
    setValue('');
  }

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        input.current?.focus();
        setOpen(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const bookings = useQuery({
    queryKey: ['search', 'bookings'],
    enabled: open && active && has('booking.view'),
    staleTime: 60_000,
    queryFn: async ({ signal }) => loadBookings(client, {}, signal),
  });
  const customers = useQuery({
    queryKey: ['search', 'customers', query],
    enabled: open && active && has('customer.basic_view'),
    staleTime: 30_000,
    queryFn: async ({ signal }) =>
      readResponse(await client.searchCustomers({ query: { text: query, limit: '5' } }, { signal })).customers,
  });
  const units = useQuery({
    queryKey: ['search', 'units'],
    enabled: open && active && has('booking.view'),
    staleTime: 60_000,
    queryFn: async ({ signal }) => readResponse(await client.listUnits({ query: {} }, { signal })).units,
  });

  const q = query.toLowerCase();
  const bookingHits = (bookings.data ?? []).filter((b) => b.displayReference.toLowerCase().includes(q)).slice(0, 4);
  const customerHits = (customers.data ?? []).filter((c) => !c.mergedIntoCustomerId).slice(0, 4);
  const unitHits = (units.data ?? [])
    .filter((u) => !u.archived && u.unitNumber.toLowerCase().startsWith(q))
    .slice(0, 4);
  const pending = bookings.isFetching || customers.isFetching || units.isFetching;
  const failed = bookings.isError || customers.isError || units.isError;
  const empty = bookingHits.length + customerHits.length + unitHits.length === 0;

  return (
    <div className="er-search">
      <label className="er-inputgroup">
        <Icon name="search" />
        <input
          ref={input}
          type="search"
          role="combobox"
          aria-label={t.label}
          aria-expanded={open && active}
          aria-controls={listId}
          aria-autocomplete="list"
          placeholder={t.placeholder}
          autoComplete="off"
          value={value}
          onChange={(event) => {
            setValue(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              setOpen(false);
              input.current?.blur();
            }
          }}
        />
        <span className="er-kbd" aria-hidden="true">
          Ctrl K
        </span>
      </label>
      {open && active ? (
        <div className="er-menu er-search__results" id={listId}>
          {bookingHits.length > 0 && <div className="er-search__group">{t.bookings}</div>}
          {bookingHits.map((b) => (
            <Link key={b.id} className="er-menu__item" to={`/bookings/${b.id}`}>
              <Icon name="calendar" />
              <span className="er-mono">{b.displayReference}</span>
              <span className="er-search__meta">{STATUS_LABELS[b.status]?.[locale] ?? b.status}</span>
            </Link>
          ))}
          {customerHits.length > 0 && <div className="er-search__group">{t.customers}</div>}
          {customerHits.map((c) => (
            <Link key={c.id} className="er-menu__item" to={`/customers/${c.id}`}>
              <Icon name={c.type === 'company' ? 'building' : 'user'} />
              <span className="er-clip">{c.name}</span>
              <span className="er-search__meta er-mono">{c.displayReference}</span>
            </Link>
          ))}
          {unitHits.length > 0 && <div className="er-search__group">{t.units}</div>}
          {unitHits.map((u) => (
            <Link key={u.id} className="er-menu__item" to={canProperty ? `/property/units/${u.id}` : '/board'}>
              <Icon name="building" />
              <span dir="ltr">{u.unitNumber}</span>
            </Link>
          ))}
          {empty ? (
            <div className="er-empty" style={{ padding: 14 }} role="status">
              {pending ? t.searching : failed ? t.failed : t.none}
            </div>
          ) : null}
        </div>
      ) : null}
      {open && active ? (
        <div className="er-search__backdrop" aria-hidden="true" onClick={() => setOpen(false)} />
      ) : null}
    </div>
  );
}
